export type DocumentStatus =
  | 'draft'
  | 'transcribing'
  | 'bot_scheduled'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'bot_failed'
  | 'transcription_failed'

export type BotStatus = 'waiting' | 'in_call' | 'processing' | 'completed' | 'failed'

interface StatusBadgeProps {
  status?: DocumentStatus | string
  botStatus?: BotStatus | string
  showDescription?: boolean
}

interface StatusConfig {
  label: string
  description: string
  badgeClass: string
  spinner?: boolean
}

/**
 * status + botStatus を組み合わせて処理パイプラインの状態を判定する
 * Bot参加待ち → 録音中 → 処理中 → 文字起こし中 → AI整形中 → 完了 / 失敗
 */
export function resolvePipelineStatus(status?: string, botStatus?: string): StatusConfig {
  // 失敗系はまとめて「失敗」
  if (status === 'failed' || status === 'bot_failed' || status === 'transcription_failed' || (status === 'bot_scheduled' && botStatus === 'failed')) {
    return { label: '失敗', description: '処理に失敗しました', badgeClass: 'badge-danger' }
  }

  switch (status) {
    case 'bot_scheduled':
      if (botStatus === 'in_call') {
        return { label: '録音中', description: 'Botが会議を録音中', badgeClass: 'badge-info', spinner: true }
      }
      if (botStatus === 'processing' || botStatus === 'completed') {
        return { label: '処理中', description: '録音データを処理中', badgeClass: 'badge-warning', spinner: true }
      }
      // waiting またはbotStatus未設定
      return { label: 'Bot参加待ち', description: 'Botの会議参加を待機中', badgeClass: 'badge-info' }
    case 'transcribing':
      return { label: '文字起こし中', description: '音声を文字起こし中', badgeClass: 'badge-warning', spinner: true }
    case 'processing':
      return { label: 'AI整形中', description: 'AIが議事録を整形中', badgeClass: 'badge-warning', spinner: true }
    case 'completed':
      return { label: '完了', description: '処理完了', badgeClass: 'badge-success' }
    case 'draft':
    default:
      return { label: 'ドラフト', description: '初期作成状態', badgeClass: 'badge-neutral' }
  }
}

export default function StatusBadge({ status = 'draft', botStatus, showDescription = false }: StatusBadgeProps) {
  const config = resolvePipelineStatus(status, botStatus)

  // 完了・ドラフトは定常状態なので目立たせない（背景なし・薄グレー文字）
  if (config.label === '完了' || config.label === 'ドラフト') {
    return (
      <span className="text-xs" style={{ color: 'var(--color-text-tertiary)', opacity: 0.85 }}>
        {config.label}
      </span>
    )
  }

  return (
    <span className={`badge ${config.badgeClass}`}>
      {config.spinner && <span className="spinner sm" aria-hidden="true" />}
      <span>{config.label}</span>
      {showDescription && (
        <span className="text-tertiary">
          ({config.description})
        </span>
      )}
    </span>
  )
}
